import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  fetchCitizensBySearchParams,
  updateSearchValue,
} from "../redux/reducer/citizensReducer";
import Header from "../components/header";
import HeaderSection from "../components/header-section";
import ButtonSubmit from "../components/common/buttonSubmit";
import TitleModal from "../components/common/titleModal";
import CitizenListTable from "../components/citizenListTable";
import SuccessAlertMessage from "../components/alertMessageModal/SuccessAlertMessage";
import SearchInput from "../components/survey/surveyFilterBox/searchInput";
import { validateFiscalCode } from "../utils/helpers";
import FiscalCodeInputFiled from "../components/common/fiscalCodeInputFiled";

// Component starts
const Enrollment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * Get url params.
   */
  const [searchParams] = useSearchParams();
  const notification = searchParams.get("notification");

  const [notificationType, setNotificationType] = useState(notification);

  /**
   * redux hooks,
   */
  const { namePharmacy, user, eventHash } = useSelector(
    (state: any) => state.login
  );
  const { citizens } = useSelector((state: any) => state);

  /**
   * state variables
   */
  const [fiscalCode, setFiscalCode] = useState(citizens.search.fiscalCode);
  const [surname, setSurname] = useState(citizens.search.surname);
  const [spinner, setSpinner] = useState(false);

  /**
   * state variables to handle errors.
   */
  const [errors, setErrors] = useState({
    surname: false,
    fiscalCode: "",
  });

  const IsDisabledSearchButton =
    surname === "" && fiscalCode === "" ? true : false;

  /**
   * Check if a search was performed and no results were found
   */
  const isNoResultFound =
    citizens?.searchPerformed && citizens?.citizensList?.length === 0;

  /**
   * update search value when it change in store.
   */
  useEffect(() => {
    if (citizens.search) {
      const { fiscalCode, surname } = citizens.search;
      setFiscalCode(fiscalCode);
      setSurname(surname);
    }
  }, [citizens.search]);

  /**
   * Search citizens by codice-fiscale or name or surname
   */
  const doSearch = async () => {
    if (handleErrors()) return false;
    setSpinner(true);
    try {
      await dispatch(
        updateSearchValue({
          fiscalCode: fiscalCode,
          surname: surname,
        })
      );
      await dispatch(fetchCitizensBySearchParams());
    } finally {
      setSpinner(false);
    }
  };

  /**
   * redirect to citizen form page
   */
  const goToCitizenFormPage = () => {
    navigate(`/citizen-form`, {
      state: { urlHistory: `/enrollment` },
    });
  };

  /**
   * update notificationType value
   */
  const handleClose = () => {
    setNotificationType("");
  };

  /**
   * checks minimum characters lenght for name and surname.
   */
  const checkMinChars = (value: string) => {
    return value && value.length < 3 ? true : false;
  };

  /**
   * checks search validations.
   */
  const handleErrors = () => {
    const fiscalCodeErr = fiscalCode ? validateFiscalCode(fiscalCode) : "";
    setErrors({
      surname: checkMinChars(surname),
      fiscalCode: fiscalCodeErr,
    });

    if (checkMinChars(surname) || fiscalCodeErr) {
      return true;
    }
    return false;
  };

  /**
   * validate fiscalcode when 16 characters reached.
   */
  const fiscalCodeLength = (value: string) => {
    if (value.length >= 16) {
      setErrors({
        ...errors,
        fiscalCode: validateFiscalCode(fiscalCode),
      });
    }
  };

  /**
   * reset fiscal code errors.
   */
  const resetFiscalCodeError = (value: string) => {
    if (value) setErrors({ ...errors, fiscalCode: "" });
  };

  return (
    <>
      {notificationType === "success" && (
        <SuccessAlertMessage
          icon="info"
          handleClose={handleClose}
          message={t("add-citizen-success")}
        />
      )}

      <Header active={true} titleLogOut={user?.username} />
      <div className="page-title">
        <HeaderSection visibleButton={false} titleSection={namePharmacy} eventHash={eventHash} />
      </div>
      <div className="container-box mb-3">
        <div className="survey-wrapper-box">
          <div className="col-md-4">
            <TitleModal title={t("find-fiscal-code")} />

            {/* codice fiscale field */}
            <div>
              <label
                className={`ms-2 label-sm ${fiscalCode && errors.fiscalCode ? "text-danger" : ""
                  }`}
              >
                {t("fiscale-code")}
              </label>

              <FiscalCodeInputFiled
                value={fiscalCode}
                handleChange={(e: any) => (
                  setFiscalCode(e), fiscalCodeLength(e), resetFiscalCodeError(e)
                )}
                className={
                  fiscalCode && errors.fiscalCode ? "border border-danger" : ""
                }
              />
              {fiscalCode && errors.fiscalCode && (
                <label className="ms-2 label-sm text-danger">
                  {errors.fiscalCode}
                </label>
              )}
            </div>

            {/* surname field */}
            <div>
              <label
                className={`ms-2 label-sm ${errors.surname && surname.length < 3 ? "text-danger" : ""
                  }`}
              >
                {t("surname")}
              </label>
              <SearchInput
                value={surname}
                onChange={(e: any) => (
                  setSurname(e),
                  e === "" ? setErrors({ ...errors, surname: false }) : () => { }
                )}
                className={
                  errors.surname && surname.length < 3
                    ? "border border-danger"
                    : ""
                }
              />
              {errors.surname && surname.length < 3 && (
                <label className="ms-2 label-sm text-danger">
                  {t("min-chars")}
                </label>
              )}
            </div>

            <div className="mt-4">
              <ButtonSubmit
                disabled={IsDisabledSearchButton}
                className="w-100"
                textButton={!spinner ? t("search") : ""}
                spinner={spinner}
                handleChange={() => doSearch()}
              />
            </div>

            <div className="mt-4 text-link" onClick={goToCitizenFormPage}>
              {t("add-citizen")}
            </div>

            {/* Display an error message when no results are found in the search */}
            {isNoResultFound && (
              <div className="warning-box mt-2">
                <span className="material-icons warning-icon-size text-warning">
                  warning
                </span>
                <span className="warning-text-size text-warning ms-2">
                  {t("no-results")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Render citizens if search data is found */}
      {citizens?.citizensList?.length > 0 && <CitizenListTable />}
    </>
  );
};

export default Enrollment;
